import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiAddLine, RiTaskLine } from '@remixicon/react'
import { TASK_PRIORITIES, type Task, type TaskPriority, type TaskStatus } from 'shared'
import { Alert, Badge, Button, Card, EmptyState, Field, Input, SectionSpinner, Select } from '../ui'
import PanelRow from './PanelRow'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { TASK_STATUS_TONE } from '../../lib/status'
import { formatDate } from '../../lib/dates'

export default function WorkspaceTasksPanel({ workspaceId }: { workspaceId: string }) {
  const { t, i18n } = useTranslation()

  const [tasks, setTasks] = useState<Task[]>([])
  const [title, setTitle] = useState('')
  const [priority, setPriority] = useState<TaskPriority>('normal')
  const [dueAt, setDueAt] = useState('')
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const response = await api.get<{ data: Task[] }>(`/api/workspaces/${workspaceId}/tasks`)
    setTasks(response.data)
  }, [workspaceId])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [load])

  const create = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!title.trim()) return

    setCreating(true)
    setError(null)

    try {
      await api.post(`/api/workspaces/${workspaceId}/tasks`, {
        title: title.trim(),
        priority,
        due_at: dueAt || null,
      })
      setTitle('')
      setDueAt('')
      setPriority('normal')
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setCreating(false)
    }
  }

  const move = async (task: Task, status: TaskStatus) => {
    setError(null)
    try {
      await api.put(`/api/workspaces/${workspaceId}/tasks/${task.id}`, { status })
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  if (loading) return <SectionSpinner />

  return (
    <div className="space-y-6">
      {error && <Alert>{error}</Alert>}

      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t('tasks.newTitle')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('tasks.newDescription')}
        </p>

        <form onSubmit={create} className="mt-4 grid gap-3 sm:grid-cols-[2fr,1fr,1fr,auto]">
          <Field label={t('tasks.fields.title')} required>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('tasks.titlePlaceholder')}
            />
          </Field>
          <Field label={t('tasks.fields.priority')}>
            <Select
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
              options={TASK_PRIORITIES.map((value) => ({
                value,
                label: t(`tasks.priority_${value}`),
              }))}
            />
          </Field>
          <Field label={t('tasks.fields.dueAt')}>
            <Input type="date" value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
          </Field>
          <div className="flex items-end">
            <Button type="submit" loading={creating} disabled={!title.trim()}>
              <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
              {t('tasks.create')}
            </Button>
          </div>
        </form>
      </Card>

      {tasks.length === 0 ? (
        <Card>
          <EmptyState
            icon={<RiTaskLine className="size-6" aria-hidden="true" />}
            title={t('tasks.operatorEmptyTitle')}
            description={t('tasks.operatorEmptyDescription')}
          />
        </Card>
      ) : (
        <Card className="divide-y divide-gray-100 dark:divide-gray-800">
          {tasks.map((task) => (
            <PanelRow
              key={task.id}
              kind="task"
              icon={<RiTaskLine className="size-5" aria-hidden="true" />}
              title={task.title}
              /* A task with no date and nobody on it left the meta line blank,
                 which reads as a rendering fault rather than as an absence. */
              meta={
                [
                  task.due_at && t('tasks.dueOn', { date: formatDate(task.due_at, i18n.language) }),
                  task.assigned_to_name,
                ]
                  .filter(Boolean)
                  .join(' \u00b7 ') || t('tasks.unscheduled')
              }
              badges={
                <>
                  {task.priority === 'high' && (
                    <Badge variant="warning">{t('tasks.priority_high')}</Badge>
                  )}
                  {task.created_by_agent && <Badge variant="default">{t('timeline.byAgent')}</Badge>}
                  <Badge variant={TASK_STATUS_TONE[task.status]}>
                    {t(`tasks.status_${task.status}`)}
                  </Badge>
                </>
              }
              action={
                task.status === 'draft' && (
                  <Button variant="secondary" onClick={() => move(task, 'open')}>
                    {t('tasks.open')}
                  </Button>
                )
              }
            />
          ))}
        </Card>
      )}
    </div>
  )
}
